// MachineUtilizationDashboard/MachineUtilizationDashboard.jsx

import { useMemo } from "react";

import DashboardSection from "./components/DashboardSection";
import DepartmentUtilizationChart from "./components/DepartmentUtilizationChart";
import LiveAlertsPanel from "./components/LiveAlertsPanel";
import LiveMachineGrid from "./components/LiveMachineGrid";
import MachineDashboardSkeleton from "./components/MachineDashboardSkeleton";
import MachineHeatMap from "./components/MachineHeatMap";
import MachineKpiCards from "./components/MachineKpiCards";
import MachineStatusDonut from "./components/MachineStatusDonut";
import OverallUtilizationCard from "./components/OverallUtilizationCard";
import RuntimeTargetChart from "./components/RuntimeTargetChart";
import UtilizationRankingChart from "./components/UtilizationRankingChart";
// import { useLocation } from "react-router-dom";

import {
  formatNumber,
  getMachineHeatMapData,
} from "./utils/machineDashboardUtils";

import "./MachineUtilizationDashboard.scss";
import { formatDate } from "../../../../../common/Validations";


function MachineUtilizationDashboard({
  loading = false,
  data = null,
  filters = {},
}) {
  // const location = useLocation();
  // const {
  //   data = null,
  //   filters = {},
  // } = location.state || {};

  const dashboard = useMemo(() => {
    return {
      machineKPIs: data?.machineKPIs || {},
      overallUtilization: data?.overallUtilization || {},
      liveMachineCards: Array.isArray(data?.liveMachineCards)
        ? data.liveMachineCards
        : [],
      topUtilizedMachines: Array.isArray(data?.topUtilizedMachines)
        ? data.topUtilizedMachines
        : [],
      bottomUtilizedMachines: Array.isArray(data?.bottomUtilizedMachines)
        ? data.bottomUtilizedMachines
        : [],
      departmentWiseUtilization: Array.isArray(data?.departmentWiseUtilization)
        ? data.departmentWiseUtilization
        : [],
      runtimeVsTarget: Array.isArray(data?.runtimeVsTarget)
        ? data.runtimeVsTarget
        : [],
      productionAndRejectByMachine: Array.isArray(
        data?.productionAndRejectByMachine,
      )
        ? data.productionAndRejectByMachine
        : [],
      heatMap: getMachineHeatMapData(data),
      liveAlerts: data?.liveAlerts || {},
    };
  }, [data]);

  if (loading && !data) {
    return <MachineDashboardSkeleton />;
  }

  if (!data) {
    return (
      <section className="machine-utilization-dashboard">
        <div className="mud-no-data">
          <span>
            <i className="bi bi-cpu" />
          </span>

          <h3>Machine Utilization Analytics</h3>

          <p>No machine utilization information is available.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="machine-utilization-dashboard mt-4">
      <div className="mud-hero">
        <div className="mud-hero__content">
          <div className="mud-hero__icon">
            <i className="bi bi-speedometer2" />
          </div>

          <div>
            <span className="mud-hero__eyebrow">
              REAL-TIME MANUFACTURING INTELLIGENCE
            </span>

            <h2>Machine Utilization Command Center</h2>

            <p>
              Live machine status, runtime performance, production output,
              department utilization and operational alerts in one view.
            </p>
          </div>
        </div>

        <div className="mud-hero__meta">
          <div>
            <span>Date range</span>

            <strong>
              {formatDate(filters?.fromDate || "-")}
              {filters?.fromDate !== filters?.toDate
                ? ` → ${formatDate(filters?.toDate || "-")}`
                : ""}
            </strong>
          </div>

          <div>
            <span>Machines monitored</span>

            <strong>
              {formatNumber(dashboard.machineKPIs?.totalMachines)}
            </strong>
          </div>

          <div className="mud-hero__live">
            <span className="mud-live-dot" />

            <strong>Live Factory View</strong>
          </div>
        </div>
      </div>

      <MachineKpiCards data={dashboard.machineKPIs} />

      <div className="mud-overview-grid">
        <DashboardSection
          eyebrow="PERFORMANCE"
          title="Overall Utilization"
          description="Weighted runtime achievement versus machine target duration."
        >
          <OverallUtilizationCard data={dashboard.overallUtilization} />
        </DashboardSection>

        <DashboardSection
          eyebrow="LIVE STATUS"
          title="Machine Distribution"
          description="Current running, idle and stopped machine breakdown."
        >
          <MachineStatusDonut data={dashboard.machineKPIs} />
        </DashboardSection>
      </div>

      <DashboardSection
        eyebrow="RANKING"
        title="Machine Utilization Leaders & Opportunities"
        description="Compare your strongest performers against machines with the highest improvement opportunity."
      >
        <UtilizationRankingChart
          topMachines={dashboard.topUtilizedMachines}
          bottomMachines={dashboard.bottomUtilizedMachines}
        />
      </DashboardSection>

      <div className="mud-two-column-grid">
        <DashboardSection
          eyebrow="DEPARTMENT ANALYTICS"
          title="Department-wise Utilization"
          description="Compare utilization performance across production departments."
        >
          <DepartmentUtilizationChart
            data={dashboard.departmentWiseUtilization}
          />
        </DashboardSection>

        <DashboardSection
          eyebrow="TIME PERFORMANCE"
          title="Actual Runtime vs Target"
          description="Machine runtime compared directly against planned target duration."
        >
          <RuntimeTargetChart data={dashboard.runtimeVsTarget} />
        </DashboardSection>
      </div>

      <DashboardSection
        eyebrow="SHOP FLOOR"
        title="Live Machine Monitor"
        description={`${formatNumber(
          dashboard.liveMachineCards.length,
        )} machines available for live monitoring.`}
      >
        <LiveMachineGrid machines={dashboard.liveMachineCards} />
      </DashboardSection>

      <DashboardSection
        eyebrow="ACTION CENTER"
        title="Live Machine Alerts"
        description="Operational exceptions requiring review, including high rejection, low utilization and exceeded target duration."
        className="mud-section--alerts"
      >
        <LiveAlertsPanel data={dashboard.liveAlerts} />
      </DashboardSection>

      <DashboardSection
        eyebrow="24-HOUR VISIBILITY"
        title="Machine Utilization Heat Map"
        description="Hourly runtime intensity by machine for identifying productive and inactive time windows."
        className="mud-section--heatmap"
      >
        <MachineHeatMap data={dashboard.heatMap} />
      </DashboardSection>
    </section>
  );
}

export default MachineUtilizationDashboard;
